import { Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './context/AuthContext'
import { ThemeProvider } from './context/ThemeContext'
import { ToastProvider } from './components/shared/Toast'
import OfflineBanner from './components/shared/OfflineBanner'
import ErrorBoundary from './components/shared/ErrorBoundary'
import { lazyWithReload } from './utils/lazyWithReload'

const Login          = lazyWithReload(() => import('./components/shared/Login'))
const LandingPage    = lazyWithReload(() => import('./components/customer/LandingPage'))
const CompanyWelcome = lazyWithReload(() => import('./components/marketing/CompanyWelcome'))
const CustomerLayout = lazyWithReload(() => import('./components/customer/CustomerLayout'))
const CompanyLayout  = lazyWithReload(() => import('./components/company/CompanyLayout'))
const Impressum      = lazyWithReload(() => import('./components/shared/Impressum'))
const Datenschutz    = lazyWithReload(() => import('./components/shared/Datenschutz'))
const AGB            = lazyWithReload(() => import('./components/shared/AGB'))
const ResetPassword  = lazyWithReload(() => import('./components/shared/ResetPassword'))
const PendingApproval = lazyWithReload(() => import('./components/shared/PendingApproval'))
const AdminLayout    = lazyWithReload(() => import('./components/admin/AdminLayout'))

function ProtectedRoute({ children, role }) {
  const { user, loading } = useAuth()
  if (loading) return null
  if (!user) return <Navigate to="/" replace />
  if (role && user.role !== role) return <Navigate to="/" replace />
  if (user.role === 'company' && !user.approved && window.location.pathname !== '/pending') {
    return <Navigate to="/pending" replace />
  }
  return children
}

function AppRoutes() {
  const { user, loading } = useAuth()

  if (loading) return null

  const redirectIfLoggedIn = (element) => {
    if (!user) return element
    if (user.role === 'admin') return <Navigate to="/admin" replace />
    if (user.role === 'company' && !user.approved) return <Navigate to="/pending" replace />
    return <Navigate to={user.role === 'company' ? '/company' : '/customer'} replace />
  }

  return (
    <Suspense fallback={null}>
      <Routes>
        <Route path="/"      element={redirectIfLoggedIn(<LandingPage />)} />
        <Route path="/partner" element={redirectIfLoggedIn(<CompanyWelcome />)} />
        <Route path="/login" element={redirectIfLoggedIn(<Login />)} />
        <Route
          path="/customer"
          element={
            <ProtectedRoute role="customer">
              <CustomerLayout />
            </ProtectedRoute>
          }
        />
        <Route
          path="/company"
          element={
            <ProtectedRoute role="company">
              <CompanyLayout />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pending"
          element={
            <ProtectedRoute role="company">
              <PendingApproval />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute role="admin">
              <AdminLayout />
            </ProtectedRoute>
          }
        />
        <Route path="/impressum" element={<Impressum />} />
        <Route path="/datenschutz" element={<Datenschutz />} />
        <Route path="/agb" element={<AGB />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <ToastProvider>
            <OfflineBanner />
            <AppRoutes />
          </ToastProvider>
        </AuthProvider>
      </ThemeProvider>
      </ErrorBoundary>
    </BrowserRouter>
  )
}
